import { useState } from 'react'
import { acceptLegalOffer, declineLegalTerms } from './legal-access'
import type { LegalAccessState } from './legal-access'
import { env } from '@/env'
import { resolvePublicApiUrl } from '@/shared/lib/api-url'
import { Button } from '@/shared/ui/button'
import { performLogoutRequest } from '@/routes/logout'

type PendingLegalState = Extract<LegalAccessState, { mode: 'pending' }>

export function LegalAcceptancePage({ state }: { state: PendingLegalState }) {
  const [accepted, setAccepted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    if (!accepted || saving) return
    setSaving(true)
    setError(null)
    try {
      await acceptLegalOffer(state)
      window.location.reload()
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Не удалось сохранить выбор.',
      )
      setSaving(false)
    }
  }

  async function decline() {
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      await declineLegalTerms(state)
      await performLogoutRequest()
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Не удалось сохранить выбор.',
      )
      setSaving(false)
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 py-10">
      <section className="w-full max-w-xl space-y-6 rounded-2xl border bg-card p-8">
        <h1 className="text-2xl font-semibold">
          Условия использования Amitable
        </h1>
        <p>
          Перед продолжением ознакомьтесь с действующими условиями. Редакция
          {state.accountType === 'member' ? 'условий участника' : 'оферты'}{' '}
          {state.offer.version}; редакция правил {state.aup.version}.
        </p>
        <PrivacyNotice url={state.privacy.url} />
        <LegalTermsChoice
          offerUrl={state.offer.url}
          accountType={state.accountType}
          aupUrl={state.aup.url}
          checked={accepted}
          onChange={setAccepted}
        />
        {error ? (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        ) : null}
        <Button disabled={!accepted || saving} onClick={() => void submit()}>
          {saving ? 'Сохраняем…' : 'Принять и продолжить'}
        </Button>
        <Button
          disabled={saving}
          variant="outline"
          onClick={() => void decline()}
        >
          Не принимаю и выйду
        </Button>
      </section>
    </main>
  )
}

function PrivacyNotice({ url }: { url: string }) {
  return (
    <p>
      Как мы обрабатываем данные:{' '}
      <a
        className="underline"
        href={resolvePublicApiUrl(env.VITE_API_URL, url)}
        target="_blank"
        rel="noopener noreferrer"
      >
        Политика конфиденциальности
      </a>
      . Это уведомление, а не согласие на необязательную аналитику.
    </p>
  )
}

function LegalTermsChoice({
  offerUrl,
  accountType,
  aupUrl,
  checked,
  onChange,
}: {
  offerUrl: string
  accountType: 'personal' | 'member'
  aupUrl: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1"
      />
      <span>
        Принимаю{' '}
        <a
          className="underline"
          href={resolvePublicApiUrl(env.VITE_API_URL, offerUrl)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {accountType === 'member'
            ? 'Условия участника рабочей области'
            : 'Публичную оферту'}
        </a>{' '}
        и{' '}
        <a
          className="underline"
          href={resolvePublicApiUrl(env.VITE_API_URL, aupUrl)}
          target="_blank"
          rel="noopener noreferrer"
        >
          Правила допустимого использования и ИИ
        </a>
        .
      </span>
    </label>
  )
}
